import { z } from "zod";
import { phoneSchema, type ContactGuestRange } from "./schemas";
import { legalIdentityComplete, type SitePlans, type SiteSettings, type SitePricing } from "./site";
import type { PlanLimits } from "./constants";
import { distanceSalesDoc, preContractDoc, type SaleLimits, type SalesDoc } from "./sales-docs";

/**
 * Online satış (F8b) — sipariş modeli. Ödeme altyapısı (iyzico/PayTR) backlog'da:
 * bugün sipariş `pending` düşer, ödeme havale/EFT ile alınır, admin "ödendi"
 * işaretler. Tablo: migration 053 (`orders`) + 055 (hak sütunları).
 *
 * HAK MODELİ (2026-10-14): satın alınan şey TEK ETKİNLİK — hesabın planı
 * DEĞİŞMEZ. Sipariş, limitlerinin anlık kopyasıyla (`ent_*`, sipariş anında
 * alıcının gördüğü değerler) bir düğüne bağlanır. İptal (iade) → hak düşer; veri silinmez.
 *
 * PAKET TANIMI TEK KAYNAK (2026-10-16): siparişin TÜM sayıları (misafir dahil) ve fiyatı paket
 * tanımından (`plans`) — misafir aralığı / elle misafir sınırı YOK. Tanım artınca satılmış
 * paketler de artar; satılmış paketin sayıları düşürülemez (admin-plans).
 */

export const ORDER_STATUSES = ["pending", "paid", "cancelled"] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
export const ORDER_PAYMENT_METHODS = ["manual", "gateway"] as const;
export type OrderPaymentMethod = (typeof ORDER_PAYMENT_METHODS)[number];

/** Bekleyen sipariş bu kadar gün ödenmezse günlük cron iptal eder (sözleşme: "7 gün"). */
export const ORDER_PENDING_DAYS = 7;
/** Teklifle (admin elle) açılan sipariş — sözleşme dışarıda imzalandı. */
export const ORDER_MANUAL_TERMS = "manual";
/**
 * Geçiş siparişi (2026-10-16): eski "hesap planı" (Pro/Max hesap) düğün paketlerine
 * dönüştürülürken her düğün için açılan 0 ₺ sipariş — sözleşme metni yok, numara `G…`.
 */
export const ORDER_LEGACY_TERMS = "legacy";

/** Ön bilgilendirme + mesafeli satış metinlerinin sürümü — siparişe kaydedilir. */
export const ORDER_TERMS_VERSION = "2026-10-14.2";

/** Satılan planlar (free satılmaz). */
export type SalePlan = "pro" | "max";

/**
 * Düğün başına satılan paketler (2026-10-15: "paket düğündedir"). HESAP planı olarak atanamaz —
 * teklif/havale satışı "Manuel sipariş" ile tek düğüne bağlanır. Hesap planı yalnız varsayılan
 * (ücretsiz deneme) ya da özel (kurumsal/abonelik) plandır; eski Pro/Max hesaplar dönüştürülür.
 */
export const PER_WEDDING_PLANS: readonly SalePlan[] = ["pro", "max"];
export function isPerWeddingPlan(slug: string): slug is SalePlan {
  return (PER_WEDDING_PLANS as readonly string[]).includes(slug);
}

/**
 * Landing'de "Satın al" görünür mü: admin açtı + fiyatlar görünür + satıcı
 * kimliği (ad/adres/e-posta) tam. Kimlik yoksa mesafeli satış yapılamaz.
 */
export function salesOpen(site: Pick<SiteSettings, "pricing" | "signup" | "legal">): boolean {
  return site.signup.orders && site.pricing.show && legalIdentityComplete(site.legal);
}

/**
 * Online satın alınabilir paket: fiyatlar görünür ve paketin tanımında fiyatı var (> 0).
 * Tutar HER ZAMAN sunucudaki paket tanımından — istemciye güvenilmez.
 */
export function purchasablePlan(
  pricing: Pick<SitePricing, "show">,
  plans: SitePlans,
  plan: SalePlan,
): { price: number; plan: SalePlan } | null {
  if (!pricing.show) return null;
  const price = plans[plan]?.price;
  if (typeof price !== "number" || price <= 0) return null;
  return { price, plan };
}

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), "Invalid date");

export const orderCreateSchema = z.object({
  /** Satın alınan paket — fiyat ve tüm sayılar sunucudaki paket tanımından. */
  plan: z.enum(["pro", "max"]),
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().max(254).email(),
  phone: phoneSchema,
  /** Etkinlik tarihi — hesabın/düğünün hazırlanması için. */
  eventDate: isoDate,
  /** Fatura adresi — mesafeli satış sözleşmesinde alıcı adresi. */
  billingAddress: z.string().trim().min(10).max(300),
  /** Fatura için TCKN (11) / VKN (10) — isteğe bağlı. */
  taxId: z
    .string()
    .trim()
    .regex(/^(\d{10,11})?$/)
    .optional()
    .default(""),
  /** Ön bilgilendirme formu + mesafeli satış sözleşmesi onayı. */
  accept: z.literal(true),
  /**
   * Alıcının ekranda gördüğü iki belgenin SHA-256 özeti (`salesDocsText`).
   * Sunucu aynı metni kendi ayarlarından üretir; tutmazsa 409 `terms_changed`
   * (alıcı güncel koşulları yeniden görür). Eski istemci göndermezse sunucu metni saklanır.
   */
  termsHash: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  lang: z.enum(["tr", "en", "fr", "es"]).optional(),
  /** Honeypot — dolu ise sessizce yutulur. */
  website: z.string().max(200).optional().default(""),
  turnstileToken: z.string().min(1),
});
export type OrderCreate = z.infer<typeof orderCreateSchema>;

export const orderPatchSchema = z
  .object({
    status: z.enum(["paid", "cancelled"]).optional(),
    adminNote: z.string().trim().max(1000).optional(),
    paymentNote: z.string().trim().max(300).optional(),
    /**
     * Hakkın uygulanacağı düğün (organizatörün düğünlerinden biri). `null` →
     * organizatörün oluşturacağı bir sonraki düğüne otomatik. "Ödendi" ile
     * birlikte ya da ödenmiş siparişte sonradan (taşıma) gönderilir.
     */
    applyTo: z.string().trim().min(1).max(64).nullable().optional(),
    /** "Ödendi" ile: alıcıya giriş + sözleşme kopyası e-postası (varsayılan: gönder). */
    sendEmail: z.boolean().optional(),
    /** Ödenmiş siparişte e-postayı (yeniden) gönder. */
    resendEmail: z.literal(true).optional(),
    /**
     * Alıcının hesabına bağlı OLMAYAN bir düğün (adres/slug) — ör. gelin satın
     * aldı, düğünü damat açtı. Yalnız admin; audit'te `foreignWedding`.
     */
    applyToSlug: z.string().trim().toLowerCase().min(1).max(64).optional(),
    /** Yalnız bekleyen siparişte: yanlış yazılmış ad / e-posta düzeltmesi. */
    name: z.string().trim().min(2).max(120).optional(),
    email: z.string().trim().toLowerCase().max(254).email().optional(),
    /**
     * Gerekçe — finansal etkisi olan admin kararlarında ZORUNLU (sunucu denetler): ödenmiş
     * siparişi iptal (iade), paketi başka düğüne taşıma. Deftere ve audit'e yazılır.
     */
    reason: z.string().trim().min(3).max(300).optional(),
  })
  .refine(
    (v) =>
      v.status !== undefined ||
      v.adminNote !== undefined ||
      v.paymentNote !== undefined ||
      v.applyTo !== undefined ||
      v.applyToSlug !== undefined ||
      v.resendEmail !== undefined ||
      v.name !== undefined ||
      v.email !== undefined,
    "empty",
  );

/**
 * Admin'in teklifle (elle) açtığı sipariş: tek etkinlik hak modeli. Telefon/adres bilinmiyorsa
 * boş kalabilir. Paket = admin Paketler'deki bir TANIM (Pro, Max ya da özel/kurumsal) — sayıların
 * hepsi tanımdan; elle misafir sınırı YOK (2026-10-16). Tutar liste fiyatının altındaysa ya da
 * paketin liste fiyatı yoksa `adminNote` (gerekçe) zorunlu — sunucu denetler.
 */
export const adminOrderCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().max(254).email(),
  phone: z.string().trim().max(20).optional().default(""),
  eventDate: isoDate,
  /** Paket tanımının slug'ı (varsayılan/Free paket satılmaz). */
  package: z.string().trim().min(1).max(50),
  amount: z.number().int().min(0).max(1_000_000),
  billingAddress: z.string().trim().max(300).optional().default(""),
  taxId: z.string().trim().regex(/^(\d{10,11})?$/).optional().default(""),
  /** Toplu paket: N ayrı sipariş (her biri kendi numarasıyla, tek düğün). Organizatör her düğünde birini seçer. */
  quantity: z.number().int().min(1).max(20).optional().default(1),
  adminNote: z.string().trim().max(1000).optional().default(""),
});
export type AdminOrderCreate = z.infer<typeof adminOrderCreateSchema>;

/** İptal öncesi etki önizlemesi — düğün günü yüklemeleri durdurmasın diye. */
export interface OrderImpact {
  wedding: { id: string; slug: string; title: string } | null;
  usage: { guests: number; media: number; storageBytes: number };
  /** Bu siparişle (bugün) geçerli limitler. */
  now: { guests: number; media: number; storageBytes: number };
  /** İptalden sonra düşülecek limitler (hesap planı + varsa diğer ödenmiş haklar). */
  after: { guests: number; media: number; storageBytes: number };
  /** `after` limitlerinin üstünde kalan kullanım → yeni ekleme/yükleme durur. */
  overAfter: boolean;
  /** Paket modelinde: iptalden sonra düğünün başka paketi kalmaz → askıya alınır (K6). */
  suspendedAfter?: boolean;
}
export type OrderPatch = z.infer<typeof orderPatchSchema>;

/** Organizatörün bekleyen (ödenmiş, düğüne tanımlanmamış) paketi — kendisi bir düğüne tanımlar. */
export interface PendingPackage {
  id: string;
  number: string;
  plan: SalePlan;
  /** Satılan misafir hakkı (yoksa null — 055 öncesi). */
  guests: number | null;
  paidAt: string | null;
  /** Paket tanımının adı (059; yoksa `PLAN_LABELS[plan]`). */
  label?: string | null;
}

/**
 * Bir düğünün geçerli paketi:
 *  - `order`   — düğüne bağlı ödenmiş sipariş (tek düğün hakkı, sipariş no'lu)
 *  - `trial`   — paketsiz, hesabın varsayılan (ücretsiz deneme) planı
 *  - `account` — paketsiz ama hesaba özel bir plan atanmış ("hesap geneli": eski Pro/Max hesap ya da kurumsal)
 */
export interface WeddingPackageInfo {
  /**
   * Paket modelinde (058): `order` | `free` | `legacy` (geçiş) | `suspended` (paketi geri alındı — K6).
   * Eski kural (058 yok ya da hesap henüz taşınmadı): `order` | `trial` | `account`.
   */
  source: "order" | "trial" | "account" | "free" | "legacy" | "suspended";
  /** "Pro" / "Max" (sipariş) ya da hesap planının adı. */
  label: string;
  orderNumber: string | null;
  /** Geçerli limit = max(hesap planı, sipariş hakkı). */
  limits: { guests: number; media: number; storageBytes: number; smsSegments: number; reminderSends: number };
}

/** Admin "Hesap planı → düğün paketleri" önizlemesi (kullanıcı detayı). */
export interface PlanConversionPreview {
  plan: { slug: string; label: string; maxGuests: number };
  defaultPlan: string;
  /** Varsayılan (deneme) planın misafir limiti — seçilmeyen düğünler buna düşer. */
  trialMaxGuests: number;
  /** Yalnız Pro/Max hesap dönüştürülebilir (özel plan = kurumsal, hesap geneli kalır). */
  convertible: boolean;
  weddings: Array<{
    id: string;
    slug: string;
    title: string;
    date_time: string | null;
    status: string;
    guests: number;
    /** Bu organizatör düğünün sahibi mi (ilk organizatör) — değilse dönüştürülemez. */
    owner: boolean;
    order: { number: string; plan: SalePlan } | null;
  }>;
}

/** Dönüştürme isteği — seçilen düğünler (boş = hiçbir düğüne paket verilmez, yalnız hesap varsayılana iner). */
export const planConversionSchema = z.object({
  weddingIds: z.array(z.string().min(1).max(64)).max(200),
  /** Önizlemede görülen plan — arada plan değiştiyse 409 (yanlış hak üretilmesin). */
  expectedPlan: z.string().min(1).max(64),
});
export type PlanConversion = z.infer<typeof planConversionSchema>;

/** Organizatörün kendi siparişleri ("Siparişlerim") — iç notlar YOK. */
export interface OrganizerOrderRow {
  id: string;
  number: string;
  plan: SalePlan;
  amount_try: number;
  status: OrderStatus;
  ent_max_guests: number | null;
  created_at: string;
  paid_at: string | null;
  /** Hesap planından düğün paketine geçiş kaydı (0 ₺, satış değil). */
  legacy?: boolean;
  /** Paket tanımının adı (özel/kurumsal paketler dahil; yoksa `PLAN_LABELS[plan]`). */
  package_label?: string | null;
  wedding: { slug: string; title: string } | null;
}

/** Bir siparişin düğüne tanıdığı limitler (sipariş anındaki değerlerin kopyası). */
export interface OrderEntitlement {
  maxGuests: number;
  maxMedia: number;
  maxStorageBytes: number;
  maxReminderSends: number;
  maxSmsSegments: number;
}

/** Paket tanımı → hak: TÜM sayılar tanımdan (misafir dahil). Sipariş anında kopyalanır. */
export function entitlementFor(limits: PlanLimits): OrderEntitlement {
  return {
    maxGuests: limits.maxGuestsPerWedding,
    maxMedia: limits.maxMediaPerWedding,
    maxStorageBytes: limits.maxStorageBytes,
    maxReminderSends: limits.maxReminderSends,
    maxSmsSegments: limits.maxSmsSegments,
  };
}

/**
 * Sözleşmede yazılan sınırlar — checkout (sitedeki kart = paket tanımı) ve worker
 * (`entitlementFor`) aynı kaynaktan: paket tanımı.
 */
export function saleLimitsFor(
  card: { guests: number; media: number; storageBytes: number; reminderSends?: number; smsSegments?: number },
): SaleLimits {
  return {
    guests: card.guests,
    media: card.media,
    storageBytes: card.storageBytes,
    // SMS sayıları yalnız sunucu verdiyse (eski worker'da yok → metin eski worker'ınkiyle aynı kalır).
    ...(typeof card.reminderSends === "number" && typeof card.smsSegments === "number"
      ? { reminderSends: card.reminderSends, smsSegments: card.smsSegments }
      : {}),
  };
}

/**
 * Online siparişin iki belgesi (ön bilgilendirme + mesafeli satış) — checkout
 * (önizleme + onay özeti) ve worker (sipariş anında deftere yazılan metin) BU
 * fonksiyonla üretir; girdiler aynıysa metin bayt bayt aynıdır. Alıcı alanları
 * `orderCreateSchema`'dan geçmiş (kırpılmış, küçük harf e-posta) değerler olmalı.
 */
export function buildOrderDocs(i: {
  seller: SiteSettings["legal"];
  web: string;
  retentionMonths: number;
  plan: SalePlan;
  amount: number;
  limits: SaleLimits;
  /** Özel / kurumsal paketin adı (manuel satış); yoksa Pro / Max. */
  packageLabel?: string;
  eventDate: string;
  /** Sözleşme günü — `istanbulDate()` */
  date: string;
  buyer: { name: string; email: string; phone: string; address: string; taxId?: string };
}): SalesDoc[] {
  const input = {
    seller: i.seller,
    web: i.web,
    buyer: i.buyer,
    order: {
      plan: i.plan,
      rangeLabel: orderGuestLabelTr(i.limits.guests),
      amount: i.amount,
      eventDate: i.eventDate,
      date: i.date,
      limits: i.limits,
      ...(i.packageLabel ? { packageLabel: i.packageLabel } : {}),
    },
    retentionMonths: i.retentionMonths,
  };
  return [preContractDoc(input), distanceSalesDoc(input)];
}

/**
 * Sözleşme / e-postadaki paket satırı — satılan misafir hakkından ("300
 * misafire kadar"). Checkout (sözleşme onayı) ve ödeme e-postası aynı sayıyı
 * yazar; sınırlar sonradan değişse de satılmış siparişin metni değişmez.
 */
export function orderGuestLabelTr(guests: number): string {
  return `${new Intl.NumberFormat("tr-TR").format(guests)} misafire kadar`;
}

/** Durum geçişleri: bekleyen → ödendi/iptal; ödenen → iptal (iade; hak düşer, düğün hesap planına döner). */
export function canTransition(from: OrderStatus, to: "paid" | "cancelled"): boolean {
  if (from === "pending") return true;
  return from === "paid" && to === "cancelled";
}

export interface OrderRow {
  id: string;
  number: string;
  plan: SalePlan;
  guest_range: ContactGuestRange;
  amount_try: number;
  name: string;
  email: string;
  phone: string;
  event_date: string;
  billing_address: string;
  tax_id: string | null;
  lang: string | null;
  status: OrderStatus;
  payment_method: OrderPaymentMethod;
  payment_note: string | null;
  admin_note: string | null;
  terms_version: string;
  organizer_id: string | null;
  created_at: string;
  paid_at: string | null;
  cancelled_at: string | null;
  // ─── migration 055 (yoksa undefined) ───
  /** Hakkın bağlı olduğu düğün; `null` + paid → bir sonraki düğüne uygulanacak. */
  wedding_id?: string | null;
  applied_at?: string | null;
  /** Alıcıya giriş + sözleşme e-postası en son ne zaman gitti (`null` → gitmedi). */
  emailed_at?: string | null;
  ent_max_guests?: number | null;
  ent_max_media?: number | null;
  ent_max_storage_bytes?: number | null;
  ent_max_reminder_sends?: number | null;
  ent_max_sms_segments?: number | null;
  // ─── migration 059 (yoksa undefined) ───
  /** Satılan paketin slug'ı (özel/kurumsal dahil); eski satırlarda = plan. */
  package?: string | null;
  /** Satış anındaki liste fiyatı (₺) — tutar altındaysa indirim (gerekçesi defterde). */
  list_price_try?: number | null;
  /** Paket tanımının adı (JOIN plans; yoksa null → `PLAN_LABELS[plan]`). */
  package_label?: string | null;
  /** Liste/ayrıntı sorgusunda bağlı düğünün adı (LEFT JOIN; yoksa null). */
  wedding_title?: string | null;
  wedding_slug?: string | null;
}

/** Admin "Ödendi" penceresindeki düğün seçenekleri. */
export interface OrderWeddingOption {
  id: string;
  slug: string;
  title: string;
  date_time: string | null;
  status: string;
  /** Silinmemiş misafir sayısı — hakkın misafir sınırının üstündeyse pencere uyarır. */
  guests?: number;
  /** Alıcının hesabına bağlı değil (slug ile bulundu). */
  foreign?: boolean;
}

/** İnsan okunur sipariş no: `S261013-7K3Q` (tarih + 4 karakter; karışan harfler yok). */
export function makeOrderNumber(now: Date, random: () => number = Math.random, prefix: "S" | "G" = "S"): string {
  const alphabet = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
  const d = now.toISOString().slice(2, 10).replace(/-/g, "");
  let tail = "";
  for (let i = 0; i < 4; i++) tail += alphabet[Math.floor(random() * alphabet.length)];
  return `${prefix}${d}-${tail}`;
}
